import { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const Contact = ({ urls }) => {
   const [formData, setFormData] = useState({
      name: '',
      email: '',
      contact: '',
      message: '',
   });
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [submitStatus, setSubmitStatus] = useState(null);

   const serviceID = 'service_un5i0rf';
   const templateID = 'template_sfl2zhl';
   const publicKey = 'eayGgizLVDHGlcTUz';

   const handleChange = (e) => {
      setFormData({
         ...formData,
         [e.target.name]: e.target.value,
      });
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      if (!formData.name || !formData.email) {
         setSubmitStatus('error');
         return;
      }

      setIsSubmitting(true);
      emailjs.init(publicKey);

      try {
         await emailjs.send(serviceID, templateID, formData);
         setSubmitStatus('success');
         setFormData({ name: '', email: '', contact: '', message: '' });
      } catch (error) {
         setSubmitStatus('error');
      } finally {
         setIsSubmitting(false);
         setTimeout(() => setSubmitStatus(null), 5000);
      }
   };

   return (
      <div>
         {/* Hero Section */}
         <div style={styles.hero} className="subpage-hero">
            <motion.h1
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.8, delay: 0.3 }}
               style={styles.heroTitle}
            >
               Contact
            </motion.h1>
         </div>

         {/* Contact Section */}
         <section style={styles.section}>
            <div style={styles.container}>
               <div style={styles.contactGrid}>
                  {/* Left Side - Info */}
                  <motion.div
                     initial={{ opacity: 0, x: -30 }}
                     whileInView={{ opacity: 1, x: 0 }}
                     viewport={{ once: true }}
                     transition={{ duration: 0.8 }}
                     style={styles.infoSection}
                  >
                     <h2 style={styles.infoTitle}>Get in Touch</h2>
                     <div style={styles.divider} />
                     <p style={styles.infoText}>
                        For inquiries about performances, collaborations,
                        or lessons, please feel free to reach out.
                     </p>

                     <div style={styles.socialSection}>
                        <h3 style={styles.socialTitle}>Follow</h3>
                        <div style={styles.socialLinks}>
                           <a
                              href={urls.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.socialLink}
                           >
                              <svg
                                 width="24"
                                 height="24"
                                 viewBox="0 0 24 24"
                                 fill="none"
                                 stroke="currentColor"
                                 strokeWidth="1.5"
                              >
                                 <rect x="2" y="2" width="20" height="20" rx="5" />
                                 <circle cx="12" cy="12" r="4" />
                                 <circle cx="18" cy="6" r="1.5" fill="currentColor" stroke="none" />
                              </svg>
                              <span>Instagram</span>
                           </a>
                           <a
                              href={urls.youtube}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.socialLink}
                           >
                              <svg
                                 width="24"
                                 height="24"
                                 viewBox="0 0 24 24"
                                 fill="none"
                                 stroke="currentColor"
                                 strokeWidth="1.5"
                              >
                                 <rect x="2" y="4" width="20" height="16" rx="4" />
                                 <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
                              </svg>
                              <span>YouTube</span>
                           </a>
                        </div>
                     </div>
                  </motion.div>

                  {/* Right Side - Form */}
                  <motion.div
                     initial={{ opacity: 0, x: 30 }}
                     whileInView={{ opacity: 1, x: 0 }}
                     viewport={{ once: true }}
                     transition={{ duration: 0.8 }}
                     style={styles.formSection}
                  >
                     <form onSubmit={handleSubmit} style={styles.form}>
                        <div style={styles.formGroup}>
                           <input
                              type="text"
                              name="name"
                              placeholder="Name *"
                              value={formData.name}
                              onChange={handleChange}
                              style={styles.input}
                              required
                           />
                        </div>
                        <div style={styles.formGroup}>
                           <input
                              type="email"
                              name="email"
                              placeholder="Email *"
                              value={formData.email}
                              onChange={handleChange}
                              style={styles.input}
                              required
                           />
                        </div>
                        <div style={styles.formGroup}>
                           <input
                              type="tel"
                              name="contact"
                              placeholder="Phone (Optional)"
                              value={formData.contact}
                              onChange={handleChange}
                              style={styles.input}
                           />
                        </div>
                        <div style={styles.formGroup}>
                           <textarea
                              name="message"
                              placeholder="Message"
                              value={formData.message}
                              onChange={handleChange}
                              rows="5"
                              style={styles.textarea}
                           />
                        </div>

                        <button
                           type="submit"
                           style={{
                              ...styles.submitButton,
                              ...(isSubmitting ? styles.submitButtonDisabled : {}),
                           }}
                           disabled={isSubmitting}
                        >
                           {isSubmitting ? 'Sending...' : 'Send Message'}
                        </button>

                        {submitStatus === 'success' && (
                           <motion.p
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              style={styles.successMessage}
                           >
                              Thank you! Your message has been sent.
                           </motion.p>
                        )}
                        {submitStatus === 'error' && (
                           <motion.p
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              style={styles.errorMessage}
                           >
                              Please fill in all required fields.
                           </motion.p>
                        )}
                     </form>
                  </motion.div>
               </div>
            </div>
         </section>
      </div>
   );
};

const styles = {
   hero: {
      position: 'relative',
      height: '50vh',
      minHeight: '400px',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
   },
   heroTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(2.5rem, 8vw, 5rem)',
      fontWeight: 300,
      color: 'white',
      letterSpacing: '0.3em',
      textTransform: 'uppercase',
      margin: 0,
   },
   section: {
      padding: '100px 0',
      backgroundColor: '#fafafa',
   },
   container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 5%',
   },
   contactGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '80px',
      alignItems: 'start',
   },
   infoSection: {
      paddingTop: '20px',
   },
   infoTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      fontWeight: 300,
      color: '#0a0a0a',
      marginBottom: '20px',
   },
   divider: {
      width: '60px',
      height: '1px',
      backgroundColor: '#c9a227',
      marginBottom: '30px',
   },
   infoText: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '1rem',
      fontWeight: 300,
      color: '#666',
      lineHeight: 1.8,
      marginBottom: '50px',
   },
   socialSection: {
      marginTop: '40px',
   },
   socialTitle: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.85rem',
      fontWeight: 500,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      color: '#0a0a0a',
      marginBottom: '25px',
   },
   socialLinks: {
      display: 'flex',
      gap: '30px',
   },
   socialLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#0a0a0a',
      textDecoration: 'none',
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.9rem',
      fontWeight: 300,
      transition: 'color 0.3s ease',
   },
   formSection: {
      backgroundColor: 'white',
      padding: '50px',
   },
   form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
   },
   formGroup: {
      marginBottom: '20px',
   },
   input: {
      width: '100%',
      padding: '18px 0',
      border: 'none',
      borderBottom: '1px solid #e5e5e5',
      backgroundColor: 'transparent',
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '1rem',
      fontWeight: 300,
      outline: 'none',
      transition: 'border-color 0.3s ease',
   },
   textarea: {
      width: '100%',
      padding: '18px 0',
      border: 'none',
      borderBottom: '1px solid #e5e5e5',
      backgroundColor: 'transparent',
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '1rem',
      fontWeight: 300,
      outline: 'none',
      resize: 'none',
      transition: 'border-color 0.3s ease',
   },
   submitButton: {
      marginTop: '30px',
      padding: '18px 50px',
      border: '1px solid #0a0a0a',
      backgroundColor: '#0a0a0a',
      color: 'white',
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.85rem',
      fontWeight: 500,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'all 0.4s ease',
      alignSelf: 'flex-start',
   },
   submitButtonDisabled: {
      backgroundColor: '#888',
      borderColor: '#888',
      cursor: 'not-allowed',
   },
   successMessage: {
      marginTop: '20px',
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.9rem',
      color: '#2e7d32',
   },
   errorMessage: {
      marginTop: '20px',
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.9rem',
      color: '#c62828',
   },
};

export default Contact;
